import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '@/components/feature/Logo';

const SCROLL_KEY = 'sa-sidebar-scroll-v2';
const SCROLL_DELAY_MS = 50;

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const saNavSections: NavSection[] = [
  {
    title: 'OPERACIÓN',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', path: '/sa/dashboard' },
      { id: 'radios', label: 'Radios', icon: 'ri-radio-line', path: '/sa/radios' },
      { id: 'frequencies', label: 'Frecuencias', icon: 'ri-radar-line', path: '/sa/frequencies' },
      { id: 'approvals', label: 'Aprobaciones', icon: 'ri-check-double-line', path: '/sa/radio-approvals' },
    ],
  },
  {
    title: 'FINANZAS',
    items: [
      { id: 'billing', label: 'Billing Overview', icon: 'ri-dashboard-line', path: '/sa/billing' },
      { id: 'plans', label: 'Plans & Licensing', icon: 'ri-stack-line', path: '/sa/plans' },
      { id: 'invoices', label: 'Invoices', icon: 'ri-file-list-3-line', path: '/sa/invoices' },
      { id: 'payments', label: 'Payments', icon: 'ri-money-dollar-circle-line', path: '/sa/payments' },
      { id: 'payouts', label: 'Payouts', icon: 'ri-exchange-funds-line', path: '/sa/payouts' },
      { id: 'payment-processors', label: 'Payment Processors', icon: 'ri-bank-card-line', path: '/sa/payment-processors' },
      { id: 'fees-charges', label: 'Fees & Charges', icon: 'ri-price-tag-3-line', path: '/sa/fees-charges' },
    ],
  },
  {
    title: 'MONETIZACIÓN',
    items: [
      { id: 'campaigns', label: 'Campañas', icon: 'ri-megaphone-line', path: '/sa/campaigns' },
      { id: 'ad-spaces', label: 'Ad Spaces', icon: 'ri-layout-grid-line', path: '/sa/ad-spaces' },
      { id: 'advertisers', label: 'Anunciantes', icon: 'ri-advertisement-line', path: '/sa/advertisers' },
      { id: 'own-ads', label: 'Radio Own Ads', icon: 'ri-radio-button-line', path: '/sa/own-ads' },
      { id: 'marketplace', label: 'Marketplace', icon: 'ri-store-2-line', path: '/sa/marketplace' },
      { id: 'sponsors', label: 'Sponsors', icon: 'ri-hand-heart-line', path: '/sa/sponsors' },
      { id: 'revenue-share', label: 'Revenue Share', icon: 'ri-percent-line', path: '/sa/revenue-share' },
    ],
  },
  {
    title: 'COMUNIDAD',
    items: [
      { id: 'users', label: 'Usuarios', icon: 'ri-group-line', path: '/sa/users' },
      { id: 'broadcasters', label: 'Locutores', icon: 'ri-user-voice-line', path: '/sa/broadcasters' },
      { id: 'programs', label: 'Programas', icon: 'ri-mic-line', path: '/sa/programs' },
      { id: 'live-support', label: 'Live Support', icon: 'ri-chat-3-line', path: '/sa/live-support' },
      { id: 'moderation', label: 'Moderación', icon: 'ri-shield-check-line', path: '/sa/global-moderation' },
    ],
  },
  {
    title: 'INTELIGENCIA',
    items: [
      { id: 'analytics', label: 'Analytics Overview', icon: 'ri-bar-chart-2-line', path: '/sa/analytics' },
      { id: 'radio-analytics', label: 'Radio Analytics', icon: 'ri-heart-pulse-line', path: '/sa/radio-analytics' },
      { id: 'community-analytics', label: 'Community Analytics', icon: 'ri-user-heart-line', path: '/sa/community-analytics' },
      { id: 'revenue-analytics', label: 'Revenue Analytics', icon: 'ri-money-dollar-circle-line', path: '/sa/monetization-analytics' },
      { id: 'ai-radio', label: 'IA para Radios', icon: 'ri-robot-2-line', path: '/sa/ai-radio' },
    ],
  },
  {
    title: 'PLATFORM',
    items: [
      { id: 'system-health', label: 'System Health', icon: 'ri-heart-pulse-line', path: '/sa/system-health-center' },
      { id: 'integrations', label: 'Integrations', icon: 'ri-puzzle-2-line', path: '/sa/integrations' },
      { id: 'security', label: 'Security & Audit', icon: 'ri-shield-check-line', path: '/sa/security' },
      { id: 'settings', label: 'Configuration', icon: 'ri-settings-4-line', path: '/sa/settings' },
      { id: 'maintenance', label: 'Maintenance & Backups', icon: 'ri-tools-line', path: '/sa/maintenance-center' },
      { id: 'communications', label: 'Communications', icon: 'ri-notification-3-line', path: '/sa/global-notification-center' },
      { id: 'apps', label: 'Apps & Channels', icon: 'ri-smartphone-line', path: '/sa/apps' },
      { id: 'releases', label: 'Releases', icon: 'ri-rocket-line', path: '/sa/releases' },
      { id: 'storage', label: 'Storage', icon: 'ri-hard-drive-2-line', path: '/sa/storage' },
      { id: 'verification', label: 'Verificación RFW', icon: 'ri-verified-badge-line', path: '/sa/verification' },
      { id: 'premium-domains', label: 'Dominios Premium', icon: 'ri-global-line', path: '/sa/premium-domains' },
      { id: 'permanent-qr', label: 'QR Permanente', icon: 'ri-qr-code-line', path: '/sa/permanent-qr' },
    ],
  },
  {
    title: 'LEGAL',
    items: [
      { id: 'legal-center', label: 'Centro Legal', icon: 'ri-scales-3-line', path: '/sa/legal-center' },
      { id: 'legal-templates', label: 'Plantillas Legales', icon: 'ri-file-copy-line', path: '/sa/legal-templates' },
      { id: 'contracts', label: 'Contratos', icon: 'ri-file-text-line', path: '/sa/contracts' },
    ],
  },
];

export default function SASidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const mobileNavRef = useRef<HTMLElement>(null);
  const prevPathname = useRef(location.pathname);

  /* ─── Notifications count ─── */
  const unreadCount = 5;

  /* ─── Close mobile drawer on path change ─── */
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const saveScroll = useCallback((nav: HTMLElement | null) => {
    if (!nav) return;
    sessionStorage.setItem(SCROLL_KEY, String(nav.scrollTop));
  }, []);

  /* ─── Smart scroll: restore only if active item visible, else scroll to it ─── */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const saved = sessionStorage.getItem(SCROLL_KEY);
    const savedTop = saved ? Number(saved) : 0;

    const activeLink = nav.querySelector('[data-sidebar-active="true"]') as HTMLElement | null;

    if (!activeLink) {
      nav.scrollTop = savedTop;
      return;
    }

    const navHeight = nav.clientHeight;
    const itemTop = activeLink.offsetTop;
    const itemHeight = activeLink.offsetHeight;

    const visibleAtSaved =
      itemTop >= savedTop + 8 &&
      itemTop + itemHeight <= savedTop + navHeight - 8;

    if (visibleAtSaved) {
      nav.scrollTop = savedTop;
    } else {
      const target = itemTop - navHeight / 2 + itemHeight / 2;
      nav.scrollTop = Math.max(0, target);
      setTimeout(() => {
        if (nav) sessionStorage.setItem(SCROLL_KEY, String(nav.scrollTop));
      }, 200);
    }

    prevPathname.current = location.pathname;
  }, [location.pathname]);

  /* ─── Scroll event: debounced save ─── */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    let timer: ReturnType<typeof setTimeout>;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => saveScroll(nav), SCROLL_DELAY_MS);
    };

    nav.addEventListener('scroll', handler, { passive: true });
    return () => {
      nav.removeEventListener('scroll', handler);
      clearTimeout(timer);
    };
  }, [saveScroll]);

  /* ─── Mobile scroll save ─── */
  useEffect(() => {
    const nav = mobileNavRef.current;
    if (!nav) return;

    let timer: ReturnType<typeof setTimeout>;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => saveScroll(nav), SCROLL_DELAY_MS);
    };

    nav.addEventListener('scroll', handler, { passive: true });
    return () => {
      nav.removeEventListener('scroll', handler);
      clearTimeout(timer);
    };
  }, [saveScroll]);

  /* ─── Shared nav item renderer ─── */
  const renderItem = (item: NavItem, isMobile: boolean) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');

    return (
      <Link
        key={item.id}
        to={item.path}
        data-sidebar-active={isActive ? 'true' : 'false'}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
          isActive
            ? 'bg-primary-50 text-primary-600'
            : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-700'
        } ${collapsed && !isMobile ? 'justify-center' : ''}`}
        title={collapsed ? item.label : undefined}
      >
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            isActive
              ? 'bg-primary-100 text-primary-600'
              : 'bg-background-200 text-foreground-400'
          }`}
        >
          <i className={`${item.icon} text-sm`}></i>
        </div>
        {(!collapsed || isMobile) && (
          <>
            <span className="text-sm font-medium whitespace-nowrap flex-1">{item.label}</span>
            {isActive && (
              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
            )}
          </>
        )}
      </Link>
    );
  };

  /* ─── Render desktop nav with sections ─── */
  const renderNavSections = () => (
    <nav
      ref={navRef}
      className="flex-1 px-3 py-4 overflow-y-auto scrollbar-none space-y-4"
    >
      {saNavSections.map((section) => (
        <div key={section.title}>
          {!collapsed && (
            <div className="flex items-center gap-2 px-3 mb-2">
              <span className="text-[10px] font-bold tracking-[0.12em] text-foreground-400 uppercase">
                {section.title}
              </span>
              <div className="flex-1 h-px bg-background-200"></div>
            </div>
          )}
          {collapsed && (
            <div className="flex items-center justify-center mb-2">
              <div className="w-6 h-px bg-background-200"></div>
            </div>
          )}
          <div className="space-y-1">
            {section.items.map((item) => renderItem(item, false))}
          </div>
        </div>
      ))}
    </nav>
  );

  /* ─── Render mobile nav with sections ─── */
  const renderMobileNavSections = () => (
    <nav
      ref={mobileNavRef}
      className="flex-1 px-3 py-4 overflow-y-auto scrollbar-none space-y-4"
    >
      {saNavSections.map((section) => (
        <div key={section.title}>
          <div className="flex items-center gap-2 px-3 mb-2">
            <span className="text-[10px] font-bold tracking-[0.12em] text-foreground-400 uppercase">
              {section.title}
            </span>
            <div className="flex-1 h-px bg-background-200"></div>
          </div>
          <div className="space-y-1">
            {section.items.map((item) => renderItem(item, true))}
          </div>
        </div>
      ))}
    </nav>
  );

  /* ─── Header ─── */
  const renderHeader = (isMobile: boolean) => (
    <div className="flex items-center gap-3 px-5 h-16 border-b border-background-200 shrink-0">
      {isMobile ? (
        <Link to="/sa/dashboard" className="flex items-center gap-3 cursor-pointer min-w-0" onClick={() => setMobileOpen(false)}>
          <Logo size="sm" />
          <div className="min-w-0 overflow-hidden">
            <h1 className="text-foreground-950 font-bold text-sm leading-tight truncate">
              Super Admin
            </h1>
            <p className="text-foreground-400 text-[10px]">RadioFrecuenciaWeb</p>
          </div>
        </Link>
      ) : (
        <>
          <Link to="/sa/dashboard" className="flex items-center gap-3 cursor-pointer min-w-0">
            <Logo size="sm" />
            {!collapsed && (
              <div className="min-w-0 overflow-hidden">
                <h1 className="text-foreground-950 font-bold text-sm leading-tight truncate">
                  Super Admin
                </h1>
                <p className="text-foreground-400 text-[10px]">RadioFrecuenciaWeb</p>
              </div>
            )}
          </Link>
          {/* Global actions */}
          <div className="ml-auto flex items-center gap-1">
            <Link
              to="/sa/notifications"
              className="relative w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              title="Notificaciones"
            >
              <i className="ri-notification-3-line text-foreground-500 text-xs"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-accent-500 border border-background-50 flex items-center justify-center">
                  <span className="text-background-50 text-[7px] font-bold leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>
                </span>
              )}
            </Link>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
              title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            >
              <i
                className={`${
                  collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'
                } text-foreground-500 text-xs`}
              ></i>
            </button>
          </div>
        </>
      )}
    </div>
  );

  /* ─── Footer ─── */
  const renderFooter = () => (
    <div className="px-3 py-4 border-t border-background-200 shrink-0">
      <div
        className={`flex items-center gap-3 p-2 rounded-xl bg-background-100 ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-foreground-950 flex items-center justify-center shrink-0">
          <span className="text-background-50 text-[10px] font-bold">SA</span>
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-foreground-800 text-xs font-semibold truncate">Admin Global</p>
            <p className="text-foreground-400 text-[10px] truncate">superadmin@rfw.com</p>
          </div>
        )}
        {!collapsed && (
          <Link
            to="/admin/dashboard"
            className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
            title="Ir a Admin Radio"
          >
            <i className="ri-user-settings-line text-foreground-500 text-xs"></i>
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* ─── DESKTOP SIDEBAR ─── */}
      <aside
        className={`hidden lg:flex flex-col bg-background-50 border-r border-background-200 h-screen sticky top-0 transition-all duration-300 ${
          collapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
      >
        {renderHeader(false)}
        {renderNavSections()}
        {renderFooter()}
      </aside>

      {/* ─── MOBILE HAMBURGER ─── */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-50 w-10 h-10 rounded-xl bg-background-50 border border-background-200 shadow-sm flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors"
        aria-label="Abrir menú"
      >
        <i className="ri-menu-line text-foreground-600 text-lg"></i>
      </button>

      {/* ─── MOBILE DRAWER ─── */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-[60] bg-black/40 transition-opacity"
            onClick={() => setMobileOpen(false)}
          ></div>

          {/* Drawer panel */}
          <aside
            className={`lg:hidden fixed inset-y-0 left-0 z-[70] w-[280px] max-w-[85vw] bg-background-50 border-r border-background-200 flex flex-col shadow-xl transform transition-transform duration-300 ${
              mobileOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {renderHeader(true)}
            {renderMobileNavSections()}
            {renderFooter()}
          </aside>
        </>
      )}
    </>
  );
}